<template>
  <v-form
    lazy-validation
    v-model="valid"
    @submit.prevent="submit"
    ref="form"
  >
      <v-container class="text-center">
        <v-btn text icon color="success" @click="refresh">
          <v-icon>{{ mdiRefresh }}</v-icon>
        </v-btn>
        <GmapMap ref="map"
          :center="mapPos"
          :zoom="12"
          map-type-id="terrain"
          style="width: 100%; height: 400px"
          :options="{
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: true,
            disableDefaultUi: false
          }"
        >
          <GmapInfoWindow
            :position="infoWindowPos"
            :opened="infoWinOpen"
            @closeclick="infoWinOpen=false"
          >
            <div v-html="selectedPoi.description" />
              <span
                class="d-block text-subtitle-2 text-right"
                style="font-size: smaller !important;"
              >
                {{ formatPoiFooter(selectedPoi) }}
              </span>
          </GmapInfoWindow>
         <GmapMarker
            v-for="(pointOfInterest, i) in pointsOfInterest"
            :key="pointOfInterest.id"
            :title="$$t(pointOfInterest.pointOfInterestType)"
            :label="$$t(pointOfInterest.pointOfInterestType)"
            :clickable="true"
            :draggable="false"
            :position="pointOfInterest.location"
            :options="{
              strokeColor: localMyLocation.strokeColour,
            }"
            @click="toggleInfoWindow(pointOfInterest, i)"
            @dblclick="removePointOfInterest(pointOfInterest)"
          />
        </GmapMap>
      </v-container>
  </v-form>
</template>

<script>
import { gmapApi } from 'vue2-google-maps';
import { mapActions } from 'vuex';
import { mdiRefresh } from '@mdi/js';
import org from '@codehq/aurora-app-core/src/mixins/org';
import location from '../mixins/location';

export default {
  name: 'FormMyLocation',
  module: 'MyLocation',
  mixins: [org, location],
  props: {
    /**
    * Set the text box value. Default: undefined,
    */
    MyLocation: {
      type: Object,
      default: undefined,
    },
  },
  data() {
    return {
      mdiRefresh,
      selectedPoi: {},
      /**
      * Hold form validation state
      */
      valid: false,
      /**
      * Local value used for property localMyLocation
      */
      localMyLocation: {},
      mapPos: { lat: -28, lng: 26 },
      pointsOfInterest: undefined,
      currentMidx: null,
      infoWindowPos: null,
      infoWinOpen: false,
      infoOptions: {
        content: '',
        // optional: offset infowindow so it visually sits nicely on top of our marker
        pixelOffset: {
          width: 0,
          height: -35,
        },
      },
    };
  },
  computed: {
    google: gmapApi,
  },
  watch: {
    MyLocation() {
      this.localMyLocation = this.MyLocation;
    },
  },
  created() {
    this.localMyLocation = this.MyLocation;
    this.localMyLocation.locationNotes = '';
    this.$on('location.changed', this.locationChanged);
  },
  destroyed() {
    this.$off('location.changed');
  },
  mounted() {
    this.refresh();
  },
  methods: {
    ...mapActions('mapItems', ['LOAD_myLocation']),
    ...mapActions('pointsOfInterest', ['LOAD_pointsOfInterest']),
    formatPoiFooter(selectedPoi) {
      return `${selectedPoi.location?.lat().toFixed(2) ?? 0}, ${selectedPoi.location?.lng().toFixed(2) ?? 0}`;
    },
    async loadPointsOfInterest() {
      try {
        const pointsOfInterest = await this.LOAD_pointsOfInterest(this.localMyLocation.id);
        this.pointsOfInterest = pointsOfInterest.map((p) => ({
          description: p.description,
          mapItemId: this.localMyLocation.id,
          pointOfInterestType: p.pointOfInterestType,
          location: this.parseMapItemPoint(p.location),
          id: p.id,
        }));
      } catch (error) {
        this.$log.error(error);
      }
    },
    async locationChanged(pos) {
      if (!this.$refs.map) {
        return;
      }
      const map = await this.$refs.map.$mapPromise;
      this.$log.info(pos);
      this.mapPos = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      };

      map.panTo(this.mapPos);

      this.localMyLocation = await this.LOAD_myLocation(this.mapPos);

      await this.loadPointsOfInterest();
      const bounds = new this.google.maps.LatLngBounds();
      bounds.extend(new this.google.maps.LatLng(this.mapPos.lat, this.mapPos.lng));
      this.pointsOfInterest.forEach((p) => {
        bounds.extend(p.location);
      });
      map.fitBounds(bounds);
      // map.fitBounds(bounds);
      map.panToBounds(bounds);
    // this.markers.push({
    //   position: this.google.maps.LatLng(pos.coords.latitude, pos.coords.longitude),
    // });
    },
    toggleInfoWindow(marker, idx) {
      this.infoWindowPos = marker.location;
      this.selectedPoi = marker;

      // check if its the same marker that was selected if yes toggle
      if (this.currentMidx === idx) {
        this.infoWinOpen = !this.infoWinOpen;
      } else {
        // if different marker set infowindow to open and reset current marker index
        this.currentMidx = idx;
        this.infoWinOpen = true;
        this.$log.info('opened info window');
      }
    },
    /** Method used parse server geography to google lantLng
     */
    parseMapItemPoint(point) {
      if (!point) {
        return undefined;
      }
      const coord = point?.geography?.wellKnownText?.replace('POINT ', '')
        .replace('(', '')
        .replace(')', '');
      const coordArr = coord.split(/[ ,]+/);
      const lat = parseFloat(coordArr[1]);
      const lng = parseFloat(coordArr[0]);
      this.$log.info(coordArr);
      // eslint-disable-next-line no-undef
      const latLng = new google.maps.LatLng(lat, lng);
      return latLng;
    },
    /** Submit Form data trigger
     * @event save
     * @type {object}
     */
    submit() {
      // validate
      if (this.$refs.form.validate()) {
        this.$emit('save', this.localMyLocation);
      }
    },
    /** Cancel form trigger
     * @event cancel
     */
    cancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<docs>
# FormMyLocation example

'''js
<FormMyLocation :MyLocation="MyLocation" />
'''
</docs>
